import React from 'react';

import {
  Menu,
  MultiControl,
  MultiDropdownTokenControl,
  useMultiDropdown,
  UseMultiDropdownProps,
  DefaultItem,
} from '@adsk/alloy-react-dropdown';
import { noop } from '@adsk/alloy-react-helpers';
import Portal from '@adsk/alloy-react-portal';

import {
  defaultValueTagRenderer,
  defaultFooterRenderer,
  defaultMenuItemRenderer,
  defaultVirtualizedItemsRenderer,
  getMenuStyle,
  renderWrapper,
} from '../helpers/utils';
import { useSelectedItems, useVirtualizedHelpers } from '../hooks';
import {
  AssigneePickerDropdownProps,
  DropdownProp,
  ItemWithGroup,
  ItemWithOptionalLabel,
} from '../types';

const MultiAssigneePicker = ({
  showIcons = false,
  showValueAsTags = true,
  disabled = false,
  dropdownProps = {},
  clearable = false,
  onChange = noop,
  isLoading,
  items,
  renderControl = (props) =>
    showValueAsTags ? (
      <MultiDropdownTokenControl {...props} />
    ) : (
      <MultiControl {...props} />
    ),
  renderMenu = (props) => <Menu {...props} />,
  renderVirtualizedMenuFooter = defaultFooterRenderer,
  renderMenuItem = defaultMenuItemRenderer,
  error,
  container,
}: AssigneePickerDropdownProps) => {
  const selectedItemsProps = useSelectedItems(
    dropdownProps as DropdownProp<UseMultiDropdownProps<ItemWithOptionalLabel>>,
    items,
  );

  const { menuItems, controlProps, menuProps, setSelectedItems } =
    useMultiDropdown({
      onSelectedItemsChange: (changes) => {
        onChange((changes.selectedItems || []) as ItemWithGroup[]);
      },
      ...dropdownProps,
      ...selectedItemsProps,
      groupedItems: items,
    } as UseMultiDropdownProps<DefaultItem>);
  const { isOpen, selectedItems, getItemProps, isGroupHeader } = menuProps;
  const { parentRef, rowVirtualizer } = useVirtualizedHelpers({
    menuItems: menuItems as ItemWithGroup[],
    isGroupHeader,
    isOpen,
  });

  return (
    <>
      {renderControl({
        ...controlProps,
        error,
        itemToString: (item: ItemWithGroup | null) =>
          defaultValueTagRenderer(item as ItemWithGroup, showIcons) as string,
        isLoading,
        disabled,
      })}
      <Portal container={container}>
        {renderMenu({
          ...menuProps,
          rowVirtualizer,
          style: getMenuStyle({ isOpen, isLoading }),
          renderWrapper: renderWrapper({
            parentRef,
            isOpen,
            setSelectedItems,
            clearable,
            hasSelectedItems: selectedItems.length > 0,
            renderVirtualizedMenuFooter,
            totalSize: rowVirtualizer.totalSize,
          }),
          children: defaultVirtualizedItemsRenderer({
            rowVirtualizer,
            groupedMenuItems: menuItems as ItemWithGroup[],
            isGroupHeader,
            getItemProps,
            renderMenuItem,
          }),
        })}
      </Portal>
    </>
  );
};

export default MultiAssigneePicker;
